/**
 * ForecastConfig — V2 admin page for the "if you complete your top
 * recommendations" projection constants used by the Opportunities dashboard.
 * Reads/writes `/api/admin/forecast-config`.
 *
 * Kept intentionally conservative for compliance safety — the model layer
 * enforces bounds, but the UI copy reminds admins never to promise a specific
 * outcome to a user.
 *
 * Wave 6 Batch 3 · Cluster C — full chrome sweep onto `--ds-*` tokens +
 * `admin/_primitives` (TextInput / AccentButton / GhostButton). Every
 * testid + numeric bound (0-200 pts, 300-850 score, 0.5-3 multiplier) +
 * API payload preserved byte-exact.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import AdminLayout from "@/admin/AdminLayout";
import { useAdminPermissions } from "@/admin/PermissionsContext";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";
import { api, formatApiError } from "@/api/client";
import { TextInput, AccentButton, GhostButton } from "@/admin/_primitives";

const FIELDS = [
  { key: "projected_credit_score_bump",              label: "Projected credit-score bump (points)",         step: 1,    min: 0,   max: 200 },
  { key: "projected_funding_readiness_bump",         label: "Projected funding readiness bump (points)",    step: 1,    min: 0,   max: 200 },
  { key: "projected_mortgage_readiness_bump",        label: "Projected mortgage readiness bump (points)",   step: 1,    min: 0,   max: 200 },
  { key: "projected_partner_count_bump_when_credit_below", label: "Partner-count bump credit threshold",   step: 1,    min: 300, max: 850 },
  { key: "projected_partner_count_delta",            label: "Additional partner matches unlocked",          step: 1,    min: 0,   max: 25  },
  { key: "purchase_power_forecast_multiplier",       label: "Purchase-power forecast multiplier",           step: 0.01, min: 0.5, max: 3   },
];

export default function AdminForecastConfig() {
  const { has } = useAdminPermissions();
  const canEdit = has("edit_scoring_rubric");
  const [initial, setInitial] = useState(null);
  const [form, setForm] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [note, setNote] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/forecast-config");
      const cur = data?.forecast_assumptions || {};
      setInitial(cur);
      setForm({ ...cur });
      setDefaults(data?.defaults || null);
      setNote(data?.note || null);
      setUpdatedAt(data?.updated_at || null);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const dirty = useMemo(() => {
    if (!initial || !form) return false;
    return FIELDS.some((f) => Number(form[f.key]) !== Number(initial[f.key]));
  }, [initial, form]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      const payload = {};
      for (const f of FIELDS) {
        const n = Number(form[f.key]);
        if (Number.isFinite(n) && n !== Number(initial[f.key])) payload[f.key] = n;
      }
      const { data } = await api.patch("/admin/forecast-config", payload);
      setInitial(data?.forecast_assumptions || form);
      setForm({ ...(data?.forecast_assumptions || form) });
      setUpdatedAt(data?.updated_at || null);
      toast.success("Forecast constants saved.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const revertOne = (key) => {
    if (!defaults) return;
    setField(key, defaults[key]);
  };

  const revertAll = () => setForm({ ...initial });

  if (loading || !form) {
    return (
      <AdminLayout title="Forecast Config">
        <div className="text-sm text-[var(--ds-color-ink-muted)]">Loading…</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Forecast Config"
      subtitle="Projection constants used on the Opportunities dashboard."
      actions={
        canEdit && (
          <div className="flex items-center gap-2">
            <GhostButton
              data-testid="forecast-config-revert"
              onClick={revertAll}
              disabled={!dirty || saving}
            >
              Revert
            </GhostButton>
            <AccentButton
              data-testid="forecast-config-save"
              onClick={save}
              disabled={!dirty || saving}
            >
              {saving ? "Saving…" : "Save changes"}
            </AccentButton>
          </div>
        )
      }
    >
      <AdminPageHelp
        title="Forecast Config"
        purpose="Sets the projected bumps a user sees on Opportunities under 'If you complete your top recommendations…'."
        configures={<span>Score bump ceilings, mortgage readiness delta, purchase-power multiplier, and the credit-score threshold below which extra partner matches unlock.</span>}
        impact={<span>Every consumer viewing Opportunities sees the new numbers on their next page load. Historic user events are not rewritten.</span>}
        safety={<span>Yes. Values can be restored to platform defaults using each row's "reset" link, or by pasting the previous number back in. Model-level bounds prevent unsafe values from being saved.</span>}
      />

      {!canEdit && (
        <div
          className="mb-4 rounded-[var(--ds-radius-card)] border px-3 py-2 text-xs"
          style={{
            borderColor: "var(--ds-color-warning)",
            background: "var(--ds-color-warning-soft)",
            color: "var(--ds-color-warning)",
          }}
        >
          Your role can view these settings but cannot save changes.
        </div>
      )}

      {note ? (
        <div
          className="mb-5 rounded-[var(--ds-radius-card)] border px-3 py-2.5 text-xs leading-relaxed"
          style={{
            borderColor: "var(--ds-color-admin-border)",
            background: "var(--ds-color-surface-muted)",
            color: "var(--ds-color-ink-muted)",
          }}
          data-testid="forecast-config-note"
        >
          {note}
        </div>
      ) : null}

      <div
        className="rounded-[var(--ds-radius-card)] border overflow-hidden"
        style={{
          borderColor: "var(--ds-color-admin-border)",
          background: "var(--ds-color-surface)",
          boxShadow: "0 1px 0 0 var(--ds-color-admin-border)",
        }}
      >
        <table className="w-full text-sm">
          <thead>
            <tr
              style={{
                background: "var(--ds-color-surface-muted)",
                color: "var(--ds-color-ink-muted)",
              }}
            >
              <th className="px-4 py-2.5 text-left text-[10px] uppercase tracking-[0.16em] font-semibold">Setting</th>
              <th className="px-4 py-2.5 text-left text-[10px] uppercase tracking-[0.16em] font-semibold w-40">Value</th>
              <th className="px-4 py-2.5 text-left text-[10px] uppercase tracking-[0.16em] font-semibold w-32">Default</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {FIELDS.map((f) => {
              const val = form[f.key] ?? "";
              const def = defaults?.[f.key] ?? "—";
              const changed = Number(val) !== Number(initial[f.key]);
              return (
                <tr
                  key={f.key}
                  data-testid={`forecast-config-row-${f.key}`}
                  className="border-t"
                  style={{ borderColor: "var(--ds-color-admin-border)" }}
                >
                  <td className="px-4 py-2.5" style={{ color: "var(--ds-color-ink)" }}>{f.label}</td>
                  <td className="px-4 py-2.5">
                    <TextInput
                      type="number"
                      value={val}
                      step={f.step}
                      min={f.min}
                      max={f.max}
                      disabled={!canEdit}
                      onChange={(e) => setField(f.key, e.target.value === "" ? "" : Number(e.target.value))}
                      data-testid={`forecast-config-input-${f.key}`}
                      className="w-full tabular-nums"
                      style={{
                        borderColor: changed
                          ? "var(--ds-color-accent)"
                          : "var(--ds-color-input-border)",
                      }}
                    />
                  </td>
                  <td
                    className="px-4 py-2.5 text-xs tabular-nums"
                    style={{ color: "var(--ds-color-ink-muted)" }}
                  >
                    {def}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {canEdit && Number(val) !== Number(def) ? (
                      <button
                        type="button"
                        onClick={() => revertOne(f.key)}
                        className="text-xs font-semibold hover:underline"
                        style={{ color: "var(--ds-color-accent-strong)" }}
                      >
                        Reset
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-xs" style={{ color: "var(--ds-color-ink-muted)" }}>
        Last updated: {updatedAt ? new Date(updatedAt).toLocaleString() : "—"}
      </div>
    </AdminLayout>
  );
}
